import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import MaintenanceMode from '@/pages/MaintenanceMode';

/**
 * Wraps the entire app and short-circuits the visitor experience while
 * Maintenance Mode is enabled. Admins, the login flow and admin routes are
 * always allowed through so the platform can be operated under maintenance.
 */
// Allowed routes when maintenance is enabled for non-admins.
// (`/login` is here so admins can authenticate themselves in.)
const ALLOWED_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/resend-verification',
];

function isAllowedPath(pathname) {
  if (pathname.startsWith('/admin')) return true;
  // Webhooks are server-side; nothing to block here. Keep static pages too.
  return ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export default function MaintenanceGate({ children }) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const fetchStatus = async () => {
      try {
        const { data } = await api.get('/maintenance/status');
        if (!cancelled) setConfig(data);
      } catch {
        if (!cancelled) setConfig({ enabled: false });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    fetchStatus();
    // Re-check every 60s so an admin toggle is reflected without a full reload
    timer = setInterval(fetchStatus, 60000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  // While the auth context / status are still loading, render nothing to
  // avoid a flash of the wrong screen.
  if (!loaded || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-ruby" />
      </div>
    );
  }

  const enabled = !!config?.enabled;
  const isAdmin = !!user?.is_admin;

  if (enabled && !isAdmin && !isAllowedPath(location.pathname)) {
    return <MaintenanceMode initialConfig={config} />;
  }

  return children;
}
